import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import TagManager from 'react-gtm-module';

import {
  Address,
  BuildingWithConvertedAddresses,
  ListingSubmitErrorMessage,
  OffMarketToastMessage,
} from '../utils/types';
import { getCookie } from '../utils/utils';
import { hasStreetAddress } from '../utils/addressFormatting';
import { shareOffMarketWillingness } from '../../utils/shareUtils';
import { PropertyDetails } from './OffMarketBuyAndSellDetailsModal';

type CreatedWillingnessData = {
  eventId: string;
  buildingId: string;
  actionType: 'buy' | 'sell';
  isMatch: boolean;
  // Set when the buy-match produced exactly one conversation: we then land the
  // buyer directly in that direct message instead of the conversation list (JAA-275).
  conversationId: string | null;
};

export interface UseOffMarketIntentParams {
  selectedBuilding: BuildingWithConvertedAddresses;
  resolvedAddress?: Address | null;
  isLoggedIn: boolean;
  isLandingPage?: boolean;
  setLoginModalVisible: (visible: boolean) => void;
  refreshFeed: () => void;
  fetchListings: () => void;
  setNotificationsSubscriptionsToggle: (toggle: boolean) => void;
  // Accepts resolved text as well as a code, so failures without a code of
  // their own reach the toast instead of falling back to window.alert.
  setSlideToastIn: (toast: OffMarketToastMessage | string | null) => void;
  fetchConversations: () => void;
  couldSellSelected: boolean;
  couldBuySelected: boolean;
  setCouldSellSelected: (selected: boolean) => void;
  setCouldBuySelected: (selected: boolean) => void;
}

const handleSubmitError = (
  message: ListingSubmitErrorMessage | undefined,
  setSlideToastIn: (toast: OffMarketToastMessage | string | null) => void,
  t: TFunction,
) => {
  switch (message) {
    case 'USER_SELLER_AND_BUYER':
      setSlideToastIn('USER_SELLER_AND_BUYER');
      break;
    case 'LIMIT_REACHED':
      setSlideToastIn(t('building.offMarket.modal.sellListingsLimitReached'));
      break;
    case 'ALREADY_SELLING_IN_BUILDING':
      setSlideToastIn(t('building.offMarket.modal.alreadySellingInBuilding'));
      break;
    default:
      break;
  }
};

/**
 * Core off-market buy/sell intent logic for the Building view's CTA pills
 * (JAA-263): optimistic counts, the share-prompt → buyer/seller match →
 * /direct-messages navigation, payment-return reopen, and the
 * `jaakko:listings-changed` map-sync event.
 */
export const useOffMarketIntent = (params: UseOffMarketIntentParams) => {
  const {
    selectedBuilding,
    resolvedAddress,
    isLoggedIn,
    isLandingPage,
    setLoginModalVisible,
    refreshFeed,
    fetchListings,
    setNotificationsSubscriptionsToggle,
    setSlideToastIn,
    fetchConversations,
    couldSellSelected,
    couldBuySelected,
    setCouldSellSelected,
    setCouldBuySelected,
  } = params;

  const { t } = useTranslation();
  const history = useHistory();

  const [offMarketSellerCount, setOffMarketSellerCount] = useState<number>(0);
  const [offMarketBuyerCount, setOffMarketBuyerCount] = useState<number>(0);
  const [isLoadingOffMarketSellWillingness, setIsLoadingOffMarketSellWillingness] =
    useState<boolean>(false);
  const [isLoadingOffMarketBuyWillingness, setIsLoadingOffMarketBuyWillingness] =
    useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [currentAction, setCurrentAction] = useState<'buy' | 'sell' | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [deleteAction, setDeleteAction] = useState<'buy' | 'sell' | null>(null);
  const [showSharePrompt, setShowSharePrompt] = useState<boolean>(false);
  const [createdWillingnessData, setCreatedWillingnessData] =
    useState<CreatedWillingnessData | null>(null);
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const [pendingToastMessage, setPendingToastMessage] =
    useState<OffMarketToastMessage | null>(null);
  const [showRemoveSellReason, setShowRemoveSellReason] = useState<boolean>(false);
  const [isRemovingSell, setIsRemovingSell] = useState<boolean>(false);

  // Use building-id from URL when present (payment returns may land before the
  // building has finished loading); otherwise the selected building's id.
  const willingnessId = useMemo(() => {
    if (isLandingPage) {
      return undefined;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const buildingIdFromUrl = urlParams.get('building-id');
    return buildingIdFromUrl || selectedBuilding?.id;
  }, [isLandingPage, selectedBuilding?.id]);

  const fetchOffMarketWillingness = () => {
    setIsLoadingOffMarketSellWillingness(true);
    setIsLoadingOffMarketBuyWillingness(true);
    fetch(
      `${process.env.REACT_APP_API_URL}/off-market-willingness?buildingId=${selectedBuilding.id}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      },
    )
      .then((res) => res.json())
      .then((data) => {
        setOffMarketSellerCount(data.could_sell_count);
        setOffMarketBuyerCount(data.could_buy_count);
        setCouldSellSelected(data.could_sell_selected);
        setCouldBuySelected(data.could_buy_selected);
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setIsLoadingOffMarketSellWillingness(false);
        setIsLoadingOffMarketBuyWillingness(false);
      });
  };

  const handleOnClickCouldSell = (
    propertyDetails?: PropertyDetails,
    removalReason?: string,
    removalReasonText?: string,
  ) => {
    setIsLoadingOffMarketSellWillingness(true);
    return fetch(`${process.env.REACT_APP_API_URL}/off-market-willingness`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_access_token'),
      },
      body: JSON.stringify({
        could_sell: !couldSellSelected,
        selected_building: selectedBuilding,
        property_description: propertyDetails?.description,
        property_size: propertyDetails?.size,
        property_rooms: propertyDetails?.rooms,
        property_floor: propertyDetails?.floor,
        property_has_elevator: propertyDetails?.hasElevator,
        property_has_balcony: propertyDetails?.hasBalcony,
        property_renovation_status: propertyDetails?.renovationStatus,
        property_asking_price: propertyDetails?.askingPrice,
        property_images: propertyDetails?.images?.map((img) => ({
          id: img.id,
          url: img.url,
          thumbnailUrl: img.thumbnailUrl,
          originalUrl: img.originalUrl,
          originalThumbnailUrl: img.originalThumbnailUrl,
          enhancedUrl: img.enhancedUrl,
          enhancedThumbnailUrl: img.enhancedThumbnailUrl,
          isDisplayOptimized: img.isDisplayOptimized,
        })),
        ...(removalReason ? { removal_reason: removalReason } : {}),
        ...(removalReasonText ? { removal_reason_text: removalReasonText } : {}),
        user_agent: navigator.userAgent,
        source: 'building_modal',
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          return res.json().then((err) => Promise.reject(err));
        }
        return res.json();
      })
      .then(
        (data: {
          could_sell_count: number;
          could_sell_selected: boolean;
          subscribed: boolean;
          willingness_id?: string;
          is_new_could_sell_and_buyers?: boolean;
          is_new_could_buy_and_sellers?: boolean;
        }) => {
          setOffMarketSellerCount(data.could_sell_count);
          setIsLoadingOffMarketSellWillingness(false);
          setCouldSellSelected(data.could_sell_selected);
          setNotificationsSubscriptionsToggle(data.subscribed);
          fetchListings();
          refreshFeed();
          // Tell the map to re-fetch /off-market-listings-in-bounds so the pill
          // count updates without waiting for a pan/zoom.
          window.dispatchEvent(new CustomEvent('jaakko:listings-changed'));

          let isMatch = false;
          if (data.is_new_could_sell_and_buyers || data.is_new_could_buy_and_sellers) {
            isMatch = true;
          }

          if (data.could_sell_selected && data.willingness_id) {
            setCreatedWillingnessData({
              eventId: data.willingness_id,
              buildingId: selectedBuilding.id,
              actionType: 'sell',
              isMatch,
              conversationId: null,
            });
            setShowSharePrompt(true);

            if (offMarketBuyerCount === 0) {
              setPendingToastMessage('COULD_SELL_NO_BUYERS');
            } else if (offMarketBuyerCount > 0) {
              fetchConversations();
              setPendingToastMessage('COULD_SELL_AND_BUYERS');
            }
          } else {
            if (data.could_sell_selected && offMarketBuyerCount === 0) {
              setSlideToastIn('COULD_SELL_NO_BUYERS');
            } else if (data.could_sell_selected && offMarketBuyerCount > 0) {
              fetchConversations();
              setSlideToastIn('COULD_SELL_AND_BUYERS');
            }
          }
        },
      )
      .catch((error) => {
        handleSubmitError(error.error_code, setSlideToastIn, t);
        setIsLoadingOffMarketSellWillingness(false);
        console.error('Error:', error);
      });
  };

  /**
   * Toggles the viewer's could-buy interest for the selected building via
   * POST /off-market-willingness, then drives the share prompt, match toasts,
   * and map refresh. Known error codes (USER_SELLER_AND_BUYER,
   * COULD_BUY_LIMIT_REACHED) surface as slide toasts and close the modal.
   */
  const handleOnClickCouldBuy = (propertyDetails?: PropertyDetails) => {
    setIsLoadingOffMarketBuyWillingness(true);
    fetch(`${process.env.REACT_APP_API_URL}/off-market-willingness`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_access_token'),
      },
      body: JSON.stringify({
        could_buy: !couldBuySelected,
        selected_building: selectedBuilding,
        property_description: propertyDetails?.description,
        property_size: propertyDetails?.size,
        property_rooms: propertyDetails?.rooms,
        property_floor: propertyDetails?.floor,
        property_has_elevator: propertyDetails?.hasElevator,
        property_has_balcony: propertyDetails?.hasBalcony,
        property_renovation_status: propertyDetails?.renovationStatus,
        user_agent: navigator.userAgent,
        source: 'building_modal',
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          return res.json().then((err) => Promise.reject(err));
        }
        return res.json();
      })
      .then(
        (data: {
          could_buy_count: number;
          could_buy_selected: boolean;
          subscribed: boolean;
          willingness_id?: string;
          is_new_could_sell_and_buyers?: boolean;
          is_new_could_buy_and_sellers?: boolean;
          conversation_ids?: string[];
        }) => {
          setOffMarketBuyerCount(data.could_buy_count);
          setIsLoadingOffMarketBuyWillingness(false);
          setCouldBuySelected(data.could_buy_selected);
          setNotificationsSubscriptionsToggle(data.subscribed);
          fetchListings();
          refreshFeed();
          window.dispatchEvent(new CustomEvent('jaakko:listings-changed'));

          let isMatch = false;
          if (data.is_new_could_sell_and_buyers || data.is_new_could_buy_and_sellers) {
            isMatch = true;
          }

          if (data.could_buy_selected && data.willingness_id) {
            setCreatedWillingnessData({
              eventId: data.willingness_id,
              buildingId: selectedBuilding.id,
              actionType: 'buy',
              isMatch,
              conversationId:
                data.conversation_ids?.length === 1 ? data.conversation_ids[0] : null,
            });
            setShowSharePrompt(true);

            if (offMarketSellerCount === 0) {
              setPendingToastMessage('COULD_BUY_NO_SELLERS');
            } else if (offMarketSellerCount > 0) {
              fetchConversations();
              setPendingToastMessage('COULD_BUY_AND_SELLERS');
            }
          } else {
            if (data.could_buy_selected && offMarketSellerCount === 0) {
              setSlideToastIn('COULD_BUY_NO_SELLERS');
            } else if (data.could_buy_selected && offMarketSellerCount > 0) {
              fetchConversations();
              setSlideToastIn('COULD_BUY_AND_SELLERS');
            }
          }
        },
      )
      .catch((error) => {
        if (error.error_code === 'COULD_BUY_LIMIT_REACHED') {
          setSlideToastIn('COULD_BUY_LIMIT_REACHED');
          setModalOpen(false);
        }
        if (error.error_code === 'USER_SELLER_AND_BUYER') {
          setSlideToastIn('USER_SELLER_AND_BUYER');
          setModalOpen(false);
        }
        setIsLoadingOffMarketBuyWillingness(false);
        console.error('Error:', error);
      });
  };

  const handleCouldSellClick = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'off_market_could_sell',
        category: 'User Interaction',
        action: 'Click',
        label: 'Off Market Could Sell',
        id: selectedBuilding.id,
        name: selectedBuilding.properties.name,
      },
    });

    if (couldSellSelected) {
      setShowRemoveSellReason(true);
    } else {
      setCurrentAction('sell');
      setModalOpen(true);
    }
  };

  // Bridge every "Buy here" CTA to the new area-interest flow (JAA-278). The
  // CouldBuyFlow lives under NewLandingPage, which has no prop path down to the
  // building modal, so we hand it the anchor building via the same custom-event
  // pattern ProfileView already uses for 'jaakko:edit-area-interest'. Works for
  // empty buildings too: NewLandingPage seeds the 500 m circle from coordinates.
  // A district-only building (JAA-417) goes without its id: with no street to
  // anchor a building-level interest on, the flow starts an area interest.
  const openCouldBuyAreaFlow = () => {
    const clickedAddress = selectedBuilding.addresses?.[0];
    // Prefer the address BuildingView already resolved/corrected (addresses[0]
    // is provisional and can name a shared-wall neighbour), so the flow opens
    // with the same street/houseNumber the modal shows.
    const address = resolvedAddress ?? clickedAddress;
    const coordinates =
      selectedBuilding.centroid ?? clickedAddress?.coordinates ?? null;
    const districtOnly = !!address && !hasStreetAddress(address);
    window.dispatchEvent(
      new CustomEvent('jaakko:open-could-buy', {
        detail: {
          buildingId: districtOnly ? '' : selectedBuilding.id,
          coordinates,
          address: {
            street: address?.street ?? '',
            houseNumber: address?.houseNumber ?? '',
            city: address?.city ?? '',
            country: address?.country ?? '',
          },
        },
      }),
    );
  };

  const handleCouldBuyClick = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'off_market_could_buy',
        category: 'User Interaction',
        action: 'Click',
        label: 'Off Market Could Buy',
        id: selectedBuilding.id,
        name: selectedBuilding.properties.name,
      },
    });

    if (couldBuySelected) {
      setDeleteAction('buy');
      setShowDeleteConfirm(true);
      return;
    }
    // Already selling here: say so now rather than letting the buyer fill in the
    // whole interest and have the publish step refuse it. Same rule, same copy,
    // just enforced at the point the intent is expressed.
    if (couldSellSelected) {
      setSlideToastIn('USER_SELLER_AND_BUYER');
      return;
    }
    openCouldBuyAreaFlow();
  };

  /**
   * "Could buy" clicked on a specific seller's listing card: opens a draft chat
   * with that seller titled with the listing's own address. The click writes
   * nothing; the buyer's first message creates the conversation
   * (POST /conversations/could-buy/start). Signed-out viewers get the same
   * draft and log in at Send (JAA-325).
   */
  const handleCouldBuyForListing = async (
    sellWillingnessId: string,
    title: string,
  ) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'off_market_could_buy_listing',
        category: 'User Interaction',
        action: 'Click',
        label: 'Off Market Could Buy From Listing',
        id: selectedBuilding.id,
        sell_listing_id: sellWillingnessId,
      },
    });
    // Writing to one seller creates nothing: the pill used to POST a
    // building-level could-buy as a side effect, so asking a question about one
    // apartment listed the buyer as demand for the whole building. The chat is
    // opened straight away and the conversation is created by the first message.
    window.dispatchEvent(
      new CustomEvent('jaakko:open-draft-chat', {
        detail: { sellWillingnessId, title },
      }),
    );
  };

  const handleModalConfirm = (propertyDetails?: PropertyDetails) => {
    if (!isLoggedIn) {
      TagManager.dataLayer({
        dataLayer: {
          event: `${
            currentAction === 'sell'
              ? 'submit_sell_building_modal_not_logged_in'
              : 'submit_buy_building_modal_not_logged_in'
          }`,
          category: 'User Interaction',
          action: 'Click',
          label: `${
            currentAction === 'sell'
              ? 'Sell Submit Building Modal Not Logged In'
              : 'Buy Submit Building Modal Not Logged In'
          }`,
        },
      });
      // JAA-226: defer marketing consent for the rest of this browser session —
      // the user came here to buy/sell, not to answer a marketing prompt.
      sessionStorage.setItem('marketing_consent_deferred', '1');
      setLoginModalVisible(true);
    } else if (currentAction === 'sell') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'sell_submit_building_modal',
          category: 'User Interaction',
          action: 'Click',
          label: 'Sell Submit Building Modal',
        },
      });
      handleOnClickCouldSell(propertyDetails);
      setModalOpen(false);
    } else if (currentAction === 'buy') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'buy_submit_building_modal',
          category: 'User Interaction',
          action: 'Click',
          label: 'Buy Submit Building Modal',
        },
      });
      handleOnClickCouldBuy(propertyDetails);
      setModalOpen(false);
    }
  };

  const handleShareWillingness = async () => {
    if (!createdWillingnessData) return;
    const buildingAddress = selectedBuilding.addresses?.[0] || ({} as any);

    setIsSharing(true);
    await shareOffMarketWillingness({
      buildingId: createdWillingnessData.buildingId,
      eventId: createdWillingnessData.eventId,
      isSellInterest: createdWillingnessData.actionType === 'sell',
      address: buildingAddress,
      t,
    });

    setIsSharing(false);
    handleCloseSharePrompt();
  };

  const handleCloseSharePrompt = () => {
    const wasMatch = createdWillingnessData?.isMatch ?? false;
    const matchedConversationId = createdWillingnessData?.conversationId ?? null;

    setShowSharePrompt(false);
    setCreatedWillingnessData(null);

    if (pendingToastMessage) {
      setSlideToastIn(pendingToastMessage);
      setPendingToastMessage(null);
    }

    // On a buyer-seller match, navigate to /direct-messages so the user finds the
    // new conversation — straight into it when the match produced exactly one
    // (JAA-275), the list otherwise. The Messages modal stacks above the
    // building view (ModalOutlet order), which stays mounted underneath.
    if (wasMatch) {
      history.push(
        matchedConversationId
          ? `/direct-messages?conversation-id=${matchedConversationId}`
          : '/direct-messages',
      );
    }
  };

  const handleSellRemovalConfirm = async (reason: string, reasonText?: string) => {
    setIsRemovingSell(true);
    try {
      await handleOnClickCouldSell(undefined, reason, reasonText);
    } finally {
      setShowRemoveSellReason(false);
      setIsRemovingSell(false);
    }
  };

  const handleDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    if (deleteAction === 'buy') {
      handleOnClickCouldBuy();
    }
  };

  // Clean up the `payment` URL param after returning from a payment flow.
  useEffect(() => {
    if (!isLandingPage && modalOpen && currentAction) {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      const modalType = urlParams.get('modal');

      if (paymentStatus && modalType === currentAction) {
        const url = new URL(window.location.href);
        url.searchParams.delete('payment');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, [isLandingPage, modalOpen, currentAction]);

  // The jaa-91 landing shell hosts BuildingView with isLandingPage=true, which
  // used to kill every URL-param effect here. A deep link that names THIS
  // building is an explicit instruction and must win over that flag.
  const urlTargetsThisBuilding = () => {
    const params = new URLSearchParams(window.location.search);
    return (
      !!selectedBuilding?.id && params.get('building-id') === String(selectedBuilding.id)
    );
  };

  // On mount, reopen the details modal when returning from a payment flow, or
  // open it directly for `modal=buy|sell` deep links (incl. target-sell-id).
  useEffect(() => {
    if (!isLandingPage || urlTargetsThisBuilding()) {
      const urlParams = new URLSearchParams(window.location.search);
      const modalParam = urlParams.get('modal');
      const paymentStatus = urlParams.get('payment');
      const sessionId = urlParams.get('session_id');

      if (modalParam && (modalParam === 'sell' || modalParam === 'buy') && !modalOpen) {
        setCurrentAction(modalParam as 'sell' | 'buy');
        setModalOpen(true);
      } else if (
        (paymentStatus === 'success' || paymentStatus === 'cancelled' || sessionId) &&
        !modalOpen
      ) {
        const inferredAction = modalParam || 'sell';
        setCurrentAction(inferredAction as 'sell' | 'buy');
        setModalOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstRender = useRef(true);

  // Keep the `modal` URL param in sync with the open details modal.
  useEffect(() => {
    if (!isLandingPage && modalOpen && currentAction) {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('modal')) {
        url.searchParams.set('modal', currentAction);
        window.history.replaceState({}, '', url.toString());
      }
    } else if (
      (!isLandingPage || urlTargetsThisBuilding()) &&
      !modalOpen &&
      !isFirstRender.current
    ) {
      const url = new URL(window.location.href);
      url.searchParams.delete('modal');
      url.searchParams.delete('payment');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [isLandingPage, modalOpen, currentAction]);

  useEffect(() => {
    fetchOffMarketWillingness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding]);

  return {
    // counts + loading
    offMarketSellerCount,
    offMarketBuyerCount,
    isLoadingOffMarketSellWillingness,
    isLoadingOffMarketBuyWillingness,
    // modal/dialog state
    modalOpen,
    setModalOpen,
    currentAction,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showSharePrompt,
    createdWillingnessData,
    isSharing,
    showRemoveSellReason,
    setShowRemoveSellReason,
    isRemovingSell,
    willingnessId,
    // handlers
    handleCouldSellClick,
    handleCouldBuyClick,
    handleCouldBuyForListing,
    handleModalConfirm,
    handleShareWillingness,
    handleCloseSharePrompt,
    handleSellRemovalConfirm,
    handleDeleteConfirm,
    // Re-sync counts + the viewer's own could_sell/could_buy selection. Exposed
    // so surfaces can refresh after a listing is created elsewhere (e.g. the
    // AMP "Sell here" flow), which only dispatches `jaakko:listings-changed`.
    refetchIntent: fetchOffMarketWillingness,
  };
};

export type UseOffMarketIntentReturn = ReturnType<typeof useOffMarketIntent>;
